
import { useAuth } from "../contexts/AuthContext";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Calendar, LayoutDashboard, CheckSquare, Users, Settings, BookOpen, PenTool } from "lucide-react";

type NavItem = {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
};

const Sidebar = () => {
  const { user } = useAuth();
  
  const navItems: NavItem[] = [
    {
      title: "Dashboard",
      href: "/",
      icon: LayoutDashboard,
    },
    {
      title: "Tasks",
      href: "/tasks",
      icon: CheckSquare,
    },
    {
      title: "Schedule",
      href: "/schedule",
      icon: Calendar,
    },
    {
      title: "Users",
      href: "/users",
      icon: Users,
      roles: ["board", "teacher"],
    },
    {
      title: "Settings",
      href: "/settings",
      icon: Settings,
      roles: ["board"],
    },
  ];

  // Filter items based on user role
  const filteredNavItems = navItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role || "")
  );

  // Helper function to get portal name based on role
  const getPortalName = (role?: string) => {
    switch (role) {
      case "teacher":
        return "Teacher Portal";
      case "parent":
        return "Parent Portal";
      case "board":
        return "Board Portal";
      default:
        return "Portal";
    }
  };

  return (
    <div className="w-64 hidden md:block h-screen bg-gradient-to-b from-background via-background to-background/95 border-r">
      <div className="px-6 py-6">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-primary to-primary/70 w-10 h-10 rounded-xl flex items-center justify-center shadow-md relative overflow-hidden group">
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            <BookOpen className="text-white h-5 w-5 relative z-10" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Preschool</h2>
            <p className="text-xs text-muted-foreground">{getPortalName(user?.role)}</p>
          </div>
        </div>
      </div>
      
      <div className="px-4">
        <div className="h-px bg-border mb-6" />
        
        <nav className="space-y-1">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-all duration-200 group relative",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                )
              }
            >
              <div className={cn(
                "absolute left-0 top-0 bottom-0 w-1 rounded-r-full transition-all",
                ({ isActive }) => isActive ? "bg-primary" : "bg-transparent"
              )} />
              <item.icon className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>{item.title}</span>
              {item.roles && (
                <div className="ml-auto">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary/40" />
                </div>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
      
      <div className="absolute bottom-8 left-0 right-0 px-6">
        <div className="bg-muted/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs font-medium text-muted-foreground">System Status</span>
          </div>
          <p className="text-xs text-muted-foreground">All services operational</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
